import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
  useState,
} from "react";
import { useAtomValue } from "jotai";
import useRecorderContext from "../context/useRecorderContext";
import { resSize } from "../utils/resSize";
import FabricCanvas, { FabricCanvasRef } from "./FabricCanvas";
import { canvasTransitionSnapshotAtom } from "../store/canvasStore";

interface CanvasStageProps {
  children?: React.ReactNode;
  onClick?: (e: any) => void;
}

const CanvasStage = forwardRef<any, CanvasStageProps>(
  ({ children, onClick }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const fabricCanvasRef = useRef<FabricCanvasRef>(null);
    const [overlayFadingOut, setOverlayFadingOut] = useState(false);

    const { recordingQuality, canvasRatio, setSelectedStream, setStageRef, canvasWidth, canvasHeight } =
      useRecorderContext();
    
    const transitionSnapshot = useAtomValue(canvasTransitionSnapshotAtom);
    
    useEffect(() => {
      if (!transitionSnapshot) {
        setOverlayFadingOut(false);
      }
    }, [transitionSnapshot]);

    //const { getCanvasNodes } = useGetCanvasInfo();

    useEffect(() => {
      if (fabricCanvasRef.current) {
        setStageRef(fabricCanvasRef);
      }
    }, [setStageRef]);

    useImperativeHandle(ref, () => ({
      getStage: () => fabricCanvasRef.current?.getCanvas() || null,
      getStageContainer: () =>
        fabricCanvasRef.current?.getCanvas()?.lowerCanvasEl || null,
    }));

    const handleObjectSelected = useCallback(
      (target: any) => {
        // Handle object selection logic for Fabric.js
        // For now, we'll just deselect if background is clicked
        if (!target || target.name === "background") {
          setSelectedStream(null);
        }
      },
      [setSelectedStream]
    );

    const handleObjectModified = useCallback((target: any) => {
      // Handle object modification logic
      // Debug logging disabled to reduce console bloat
    }, []);

    // Use canvasWidth/canvasHeight from context for CSS display size
    // resSize() is used internally in FabricCanvas for backing store resolution

    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        <FabricCanvas
          ref={fabricCanvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onObjectSelected={handleObjectSelected}
          onObjectModified={handleObjectModified}
          className="canvas-stage"
        >
          {/* Children will be handled by individual components adding objects to the canvas */}
        </FabricCanvas>
        {transitionSnapshot && (
          <img
            src={transitionSnapshot.dataUrl}
            alt=""
            style={{
              position: "absolute",
              top: transitionSnapshot.offsetTop,
              left: transitionSnapshot.offsetLeft,
              width: transitionSnapshot.width,
              height: transitionSnapshot.height,
              pointerEvents: "none",
              opacity: overlayFadingOut ? 0 : 1,
              transition: "opacity 300ms ease-out",
              zIndex: 100,
            }}
            onTransitionEnd={() => {
              if (overlayFadingOut) {
                setOverlayFadingOut(false);
              }
            }}
          />
        )}
        <div className="recording-quality">
          <p>{recordingQuality}</p>
        </div>
      </div>
    );
  }
);

CanvasStage.displayName = "CanvasStage";

export default CanvasStage;
